import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SITE_URL } from "./App";

const serviceSections = [
  {
    heading: "Taxation Services",
    summary: "Income tax, NTN, STRN, sales tax and return filing support for individuals and businesses, including SRB, PRA and BRA matters.",
    services: [
      {
        title: "Income Tax Filing & Returns",
        points: ["Personal and business income tax returns", "Wealth statements and reconciliation", "Annual tax compliance reviews"],
      },
      {
        title: "NTN & STRN Registration",
        points: ["New NTN for individuals and businesses", "STRN registration and profile setup", "FBR portal access and token support"],
      },
      {
        title: "Sales Tax Return Filing",
        points: ["Monthly sales tax returns", "Input and output tax reconciliation", "SRB, PRA, BRA and FBR sales tax support"],
      },
    ],
  },
  {
    heading: "Business Registration Services",
    summary: "Company formation and entity setup with documentation, portal filing and compliance guidance.",
    services: [
      {
        title: "SECP Company Registration",
        points: ["Private Limited and Single Member Company setup", "Name reservation, MOA and AOA drafting", "Digital signatures and SECP submission"],
      },
      {
        title: "Partnership & Sole Proprietor Setup",
        points: ["Business structure guidance", "Tax registration and documentation", "Bank and compliance document support"],
      },
    ],
  },
  {
    heading: "Compliance & Legal Support",
    summary: "Practical support for notices, audit preparation, filings and documentation with tax authorities.",
    services: [
      {
        title: "FBR, SRB, PRA & BRA Compliance",
        points: ["Monthly and annual returns", "Withholding tax statements", "Correction of portal errors and notices"],
      },
      {
        title: "Tax Notice Handling",
        points: ["Notice review and response drafting", "Document alignment for tax queries", "Follow-up and representation support"],
      },
      {
        title: "Audit Preparation",
        points: ["Record organization and compliance checks", "Financial data alignment", "Documentation for tax disputes"],
      },
    ],
  },
  {
    heading: "International Bookkeeping, Reconciliation & Digital Portals",
    summary: "International bookkeeping and bank reconciliation support so records, reports and business portals stay organized.",
    services: [
      {
        title: "Bookkeeping & Bank Reconciliation",
        points: ["International bookkeeping support", "Bank reconciliations and ledgers", "Monthly accounting and profit and loss statements"],
      },
      {
        title: "PSEB Registration & Renewal",
        points: ["PSEB registration for freelancers and IT companies", "Renewals and profile updates", "Document upload and approval tracking"],
      },
      {
        title: "SME Dashboard & Reporting",
        points: ["Expense and profit visibility", "Custom reporting workflows", "Excel-based update support"],
      },
    ],
  },
];

export default function Services() {
  return (
    <>
      <Helmet>
        <title>International Bookkeeping, Tax & Compliance Services | Axis Compliance Solutions</title>
        <meta
          name="description"
          content="Explore Axis Compliance Solutions services: international bookkeeping, bank reconciliation, income tax filing, NTN, STRN, SECP registration, sales tax returns, FBR, SRB, PRA and BRA compliance, PSEB registration and audit support."
        />
        <meta
          name="keywords"
          content="international bookkeeping services, bank reconciliation services, income tax filing Pakistan, SECP company registration Lahore, FBR compliance consultant, SRB PRA BRA compliance, NTN registration Pakistan, STRN registration, sales tax return filing, PSEB registration Pakistan"
        />
        <link rel="canonical" href={`${SITE_URL}/services`} />
        <meta property="og:title" content="International Bookkeeping, Tax & Compliance Services | Axis Compliance Solutions" />
        <meta
          property="og:description"
          content="Explore income tax filing, SECP company registration, FBR compliance, sales tax, bookkeeping and bank reconciliation services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services`} />
        <meta property="og:image" content={`${SITE_URL}/services-compliance-desk.png`} />
        <meta property="og:image:alt" content="Organized tax, bookkeeping and compliance documents on a professional desk" />
        <meta name="twitter:title" content="Tax, Bookkeeping & Compliance Services | Axis Compliance Solutions" />
        <meta
          name="twitter:description"
          content="Income tax filing, company registration, FBR compliance, sales tax, bookkeeping and bank reconciliation support."
        />
        <meta name="twitter:image" content={`${SITE_URL}/services-compliance-desk.png`} />
        <meta name="twitter:image:alt" content="Organized tax, bookkeeping and compliance documents on a professional desk" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: serviceSections.flatMap((section) =>
              section.services.map((service) => ({
                "@type": "Service",
                name: service.title,
                provider: {
                  "@type": "AccountingService",
                  name: "Axis Compliance Solutions",
                },
                areaServed: "Pakistan",
              }))
            ),
          })}
        </script>
      </Helmet>

      <section className="page-hero">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <p className="eyebrow text-cyan-200">Services</p>
          <h1>International bookkeeping, tax, registration and compliance services in one place.</h1>
          <p>
            Choose focused support for international bookkeeping, bank reconciliation, FBR filing,
            SECP company registration, sales tax, audit preparation, PSEB registration and
            business compliance nationwide in Pakistan.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-12 grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <p className="eyebrow">How we work</p>
              <h2 className="section-title">Organized documentation, clear filings and practical compliance follow-through.</h2>
              <p className="mt-5 leading-8 text-slate-600">
                Each service is handled with a document-first process: collect the right records,
                check the relevant portal requirements, prepare filings and keep the next deadline visible.
              </p>
            </div>
            <div className="overflow-hidden rounded-md border border-slate-200 shadow-sm">
              <img
                src="/services-compliance-desk.png"
                alt="Organized tax, bookkeeping and compliance documents on a professional desk"
                className="h-full min-h-[280px] w-full object-cover"
              />
            </div>
          </div>
          <div className="space-y-14">
            {serviceSections.map((section) => (
              <div key={section.heading}>
                <div className="mb-7 border-l-4 border-cyan-700 pl-5">
                  <h2 className="text-2xl font-semibold text-slate-950">{section.heading}</h2>
                  <p className="mt-2 max-w-3xl text-slate-600">{section.summary}</p>
                </div>
                <div className="grid gap-5 md:grid-cols-3">
                  {section.services.map((service) => (
                    <article key={service.title} className="service-card">
                      <h3>{service.title}</h3>
                      <ul className="mt-5 space-y-3">
                        {service.points.map((point) => (
                          <li key={point} className="flex gap-3 text-sm leading-6 text-slate-600">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-700" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-slate-100">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[1fr_0.8fr] lg:items-center lg:px-8">
          <div>
            <p className="eyebrow">Not sure where to start?</p>
            <h2 className="section-title">We can map the right compliance path for your case.</h2>
            <p className="mt-4 leading-8 text-slate-600">
              Share your entity type, filing status and deadline. We will help identify whether
              you need tax filing, registration, portal correction, international bookkeeping,
              bank reconciliation or audit support.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
            <Link to="/contact" className="btn-primary">Talk to an Expert</Link>
            <Link to="/about" className="btn-secondary">Learn About Axis</Link>
          </div>
        </div>
      </section>
    </>
  );
}
